import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { ManagerRole } from '../api/managerApi';
import { DataTable, LoadingState, inlineFieldSx, useErrorToast, type DataTableColumn } from '../components/common';
import { loadMyManagerProfile } from '../services/managerService';
import { readerDeviceService, type ReaderDevice } from '../services/readerDeviceService';

const iconStroke = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const EditIcon = () => (
  <svg {...iconStroke} aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg {...iconStroke} aria-hidden="true">
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const codeFieldSx = { ...inlineFieldSx, fontSize: 12, height: 30, '& input': { textAlign: 'center' } };

function DeviceManagementPage() {
  const [devices, setDevices] = useState<ReaderDevice[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingCode, setEditingCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageDevices = managerRole === 'SUPER' || managerRole === 'MIDDLE';

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    readerDeviceService.list().then(setDevices).catch(showError).finally(() => setIsLoading(false));
  }, [showError]);

  const handleAddConfirm = async () => {
    if (!canManageDevices) return;
    if (!newCode.trim()) return;
    try {
      const created = await readerDeviceService.create(newCode.trim());
      setDevices((prev) => [...prev, created]);
      setNewCode('');
      setIsAdding(false);
    } catch (error) {
      showError(error);
    }
  };

  const handleAddCancel = () => {
    setNewCode('');
    setIsAdding(false);
  };

  const startEdit = (device: ReaderDevice) => {
    if (!canManageDevices) return;
    setIsAdding(false);
    setEditingId(device.id);
    setEditingCode(device.code);
  };

  const handleEditConfirm = async () => {
    if (!canManageDevices) return;
    if (!editingCode.trim() || editingId === null) return;
    try {
      const updated = await readerDeviceService.update(editingId, editingCode.trim());
      setDevices((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      setEditingId(null);
      setEditingCode('');
    } catch (error) {
      showError(error);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingCode('');
  };

  const handleDelete = async (device: ReaderDevice) => {
    if (!canManageDevices) return;
    try {
      await readerDeviceService.delete(device.id);
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
    } catch (error) {
      showError(error);
    }
  };

  const columns: DataTableColumn<ReaderDevice>[] = [
    {
      key: 'code',
      header: '기기 코드',
      align: 'center',
      sortable: true,
      render: (row) => {
        if (row.id !== editingId) return row.code;
        return (
          <InputBase
            autoFocus
            fullWidth
            value={editingCode}
            onChange={(e) => setEditingCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditConfirm();
              if (e.key === 'Escape') handleEditCancel();
            }}
            sx={codeFieldSx}
          />
        );
      },
    },
    ...(canManageDevices
      ? [
          {
            key: '_actions',
            header: '',
            width: 88,
            align: 'right' as const,
            render: (row: ReaderDevice) => {
              if (row.id === editingId) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" color="primary" onClick={handleEditConfirm}>✓</IconButton>
                    <IconButton size="small" onClick={handleEditCancel}>✕</IconButton>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', color: '#94a3b8' }}>
                  <IconButton
                    size="small"
                    aria-label="기기 코드 수정"
                    sx={{ color: 'inherit', '&:hover': { color: '#2563eb' } }}
                    onClick={() => startEdit(row)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="기기 코드 삭제"
                    sx={{ color: 'inherit', '&:hover': { color: '#dc2626' } }}
                    onClick={() => handleDelete(row)}
                  >
                    <TrashIcon />
                  </IconButton>
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const addRow = canManageDevices && isAdding ? (
    <TableRow>
      <TableCell align="center">
        <InputBase
          autoFocus
          fullWidth
          placeholder="입력해주세요"
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddConfirm();
            if (e.key === 'Escape') handleAddCancel();
          }}
          sx={codeFieldSx}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" color="primary" onClick={handleAddConfirm}>✓</IconButton>
          <IconButton size="small" onClick={handleAddCancel}>✕</IconButton>
        </Box>
      </TableCell>
    </TableRow>
  ) : null;

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <p className="page-section">Admin</p>
        <h1>기기 관리</h1>
        <p className="page-description">리더기 기기 코드를 등록하고 삭제합니다.</p>
        <div className="page-table">
          {isLoading ? (
            <LoadingState message="기기 목록을 불러오는 중입니다." />
          ) : (
            <DataTable
              columns={columns}
              rows={devices}
              rowKey="id"
              footer={addRow}
              emptyMessage="등록된 기기 코드가 없습니다. ‘기기 코드 추가’로 등록해 주세요."
            />
          )}
          {canManageDevices && !isAdding && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1.5, display: 'block', mx: 'auto' }}
              onClick={() => {
                setEditingId(null);
                setIsAdding(true);
              }}
            >
              + 기기 코드 추가
            </Button>
          )}
        </div>
      </div>
      {errorToast}
    </section>
  );
}

export default DeviceManagementPage;
